import { IconCheck, IconCommentAltInfo, IconTimes } from '@maia/icons';
import { CmsProductDetailsDto } from '@maia/shared';
import { get } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { match } from 'ts-pattern';
import { MarkdownRenderer, Typography } from '../../../ui';

type CmsElemens = CmsProductDetailsDto['elements'];
type CmsElement = CmsElemens[number];

const identifier = (element: CmsElement) => get(element, 'identifier', null);

const getIdentifier = (
  element: CmsElement,
  target: CmsElement['identifier'],
) => (identifier(element) === target ? element : null);

const detailsTitleElement = (element: CmsElement) =>
  getIdentifier(element, 'details_title');

export const getDetailsTitle = (element: CmsElement) =>
  getIdentifier(element, 'details_title');

const detailsListsChecklist = (element: CmsElement) =>
  getIdentifier(element, 'lists_checklist');

const getListsChecklist = (element: CmsElement) =>
  getIdentifier(element, 'lists_checklist');

const detailsContent = (element: CmsElement) =>
  getIdentifier(element, 'content');

export function ProductDetails(props: { readonly elements: CmsElemens }) {
  const { elements } = props;

  return (
    <div className="flex flex-col gap-y-32">
      <CmsMapper elements={elements} />
    </div>
  );
}

function CmsMapper(props: { readonly elements: CmsElemens }) {
  const { elements } = props;
  return elements.map((e) =>
    match(e)
      .when(detailsTitleElement, () => <DetailsHeader element={e} />)
      .when(detailsListsChecklist, () => <DetailsList element={e} />)
      .when(detailsContent, () => <DetailsContent element={e} />)
      .otherwise(() => null),
  );
}

function DetailsHeader(props: { readonly element: CmsElement }) {
  const { element } = props;
  const content = useMemo(() => getDetailsTitle(element), [element]);
  return (
    <header className="flex gap-x-12">
      <IconCommentAltInfo className="size-24" />
      <div className="flex flex-col">
        <Typography variant="display6" weight="semibold">
          {content?.fields.find((f) => f.key === 'title')?.value}
        </Typography>
        <Typography
          variant="body-m"
          weight="regular"
          className="text-grayscale300"
        >
          {content?.fields.find((f) => f.key === 'subTitle')?.value}
        </Typography>
      </div>
    </header>
  );
}

function DetailsList(props: { readonly element: CmsElement }) {
  const { element } = props;
  const content = useMemo(() => getListsChecklist(element), [element]);

  const isIncludedList =
    content?.fields.find((f) => f.key === 'included')?.value === 'true';

  return (
    <div>
      <Typography
        variant="body-m"
        weight="medium"
        className="inline-block mb-16"
      >
        {content?.fields.find((f) => f.key === 'title')?.value}
      </Typography>
      <ul className="flex flex-col gap-y-12">
        {content?.elements.map((element) => {
          return (
            <li key={element.markdown} className="flex gap-x-12">
              {isIncludedList ? (
                <IconCheck className="shrink-0 fill-secondaryMain mt-[3px]" />
              ) : (
                <IconTimes className="shrink-0 fill-error300  mt-[3px]" />
              )}
              <MarkdownRenderer markdown={element.markdown ?? ''} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DetailsContent(props: { readonly element: CmsElement }) {
  const { element } = props;
  const content = useMemo(() => detailsContent(element), [element]);
  if (!content) return null;
  return <MarkdownRenderer markdown={content.markdown} />;
}
